import { z } from 'zod'

import { readEdaInfo } from '@/components/eda-info-fields'
import { TreeScreen } from '@/features/trees/TreeScreen'

/*
 * The three tree-shaped kinds of reference data, as the catalogue shows them.
 * Moved here unchanged from their old routes (/categories, /footprints,
 * /attachment-types), which now redirect into the catalogue.
 */
const categoriesSchema = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(128),
  comment: z.string(),
  part_name_hint: z.string(),
  part_name_regex: z.string(),
  default_description: z.string(),
  default_comment: z.string(),
  disable_footprints: z.boolean(),
  disable_manufacturers: z.boolean(),
  disable_autodatasheets: z.boolean(),
  eda_info: z.record(z.string(), z.unknown()),
})

export function CategoriesBody() {
  return (
    <TreeScreen
      kind="categories"
      countLabel="parts"
      schema={categoriesSchema}
      fields={[
        { name: 'name', label: 'Name' },
        {
          name: 'part_name_hint',
          label: 'Part name hint',
          placeholder: 'R-0805-10k-1%',
          description: 'Shown as the placeholder when naming a part filed here.',
        },
        {
          name: 'part_name_regex',
          label: 'Part name pattern',
          wide: true,
          description: 'Optional regular expression part names here must match.',
        },
        { name: 'default_description', label: 'Default description', type: 'textarea' },
        { name: 'default_comment', label: 'Default comment', type: 'textarea' },
        { name: 'comment', label: 'Comment', type: 'textarea' },
        { name: 'disable_footprints', label: 'Parts here have no footprint', type: 'checkbox' },
        {
          name: 'disable_manufacturers',
          label: 'Parts here have no manufacturer',
          type: 'checkbox',
        },
        {
          name: 'disable_autodatasheets',
          label: 'Do not fetch datasheets automatically',
          type: 'checkbox',
        },
        {
          name: 'eda_info',
          label: 'KiCad defaults',
          type: 'eda',
          description:
            'Inherited by every part filed here, and by every category beneath it, unless the part says otherwise.',
        },
      ]}
      emptyValues={{
        name: '',
        comment: '',
        part_name_hint: '',
        part_name_regex: '',
        default_description: '',
        default_comment: '',
        disable_footprints: false,
        disable_manufacturers: false,
        disable_autodatasheets: false,
        eda_info: {},
      }}
      toValues={(detail) => ({
        name: String(detail.name ?? ''),
        comment: String(detail.comment ?? ''),
        part_name_hint: String(detail.part_name_hint ?? ''),
        part_name_regex: String(detail.part_name_regex ?? ''),
        default_description: String(detail.default_description ?? ''),
        default_comment: String(detail.default_comment ?? ''),
        disable_footprints: Boolean(detail.disable_footprints),
        disable_manufacturers: Boolean(detail.disable_manufacturers),
        disable_autodatasheets: Boolean(detail.disable_autodatasheets),
        eda_info: { ...readEdaInfo(detail.eda_info) },
      })}
    />
  )
}

const footprintsSchema = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(128),
  comment: z.string(),
  eda_info: z.record(z.string(), z.unknown()),
})

export function FootprintsBody() {
  return (
    <TreeScreen
      kind="footprints"
      countLabel="parts"
      schema={footprintsSchema}
      fields={[
        { name: 'name', label: 'Name', placeholder: 'SOIC-8' },
        { name: 'comment', label: 'Comment', type: 'textarea' },
        {
          name: 'eda_info',
          label: 'KiCad defaults',
          type: 'eda',
          description:
            'Only the footprint is read from here — a footprint has no business choosing a part’s symbol.',
        },
      ]}
      emptyValues={{ name: '', comment: '', eda_info: {} }}
      toValues={(detail) => ({
        name: String(detail.name ?? ''),
        comment: String(detail.comment ?? ''),
        eda_info: { ...readEdaInfo(detail.eda_info) },
      })}
    />
  )
}

const attachmentTypesSchema = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(128),
  comment: z.string(),
  filetype_filter: z.string(),
})

export function AttachmentTypesBody() {
  return (
    <TreeScreen
      kind="attachment-types"
      countLabel="attachments"
      schema={attachmentTypesSchema}
      fields={[
        { name: 'name', label: 'Name', placeholder: 'Datasheet' },
        {
          name: 'filetype_filter',
          label: 'Accepted file types',
          placeholder: '.pdf,.html',
          description: 'Comma-separated extensions. Leave empty to accept anything.',
        },
        { name: 'comment', label: 'Comment', type: 'textarea' },
      ]}
      emptyValues={{ name: '', comment: '', filetype_filter: '' }}
      toValues={(detail) => ({
        name: String(detail.name ?? ''),
        comment: String(detail.comment ?? ''),
        filetype_filter: String(detail.filetype_filter ?? ''),
      })}
    />
  )
}
